import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
}

function simplifyFrac(num, den) {
  if (den === 0) return [num, den];
  let n = num;
  let d = den;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return [n / g, d / g];
}

function fracToMixedLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  const sign = n < 0 ? "-" : "";
  const absN = Math.abs(n);
  const whole = Math.floor(absN / d);
  const rem = absN % d;
  if (whole === 0) return `${sign}\\dfrac{${absN}}{${d}}`;
  if (rem === 0) return `${sign}${whole}`;
  return `${sign}${whole}\\dfrac{${rem}}{${d}}`;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

function term(coeff, symbol, isFirst) {
  if (coeff === 0) return "";
  const abs = Math.abs(coeff);
  const core = abs === 1 ? symbol : `${abs}${symbol}`;
  if (isFirst) return coeff < 0 ? `-${core}` : core;
  return coeff < 0 ? ` - ${core}` : ` + ${core}`;
}

function buildLinearEquation(a, b, c) {
  let left = "";
  left += term(a, "x", true);
  left += term(b, "y", left.length === 0);
  if (!left) left = "0";
  return `${left} = ${c}`;
}

function absCoeffWithVar(coeff, variable) {
  const abs = Math.abs(coeff);
  return abs === 1 ? variable : `${abs}${variable}`;
}

export function generateTask() {
  let mode = "xFromY"; // x = (...) / c  OR y = (...) / c
  let fracLine = 1;
  let a = 1;
  let b = 0;
  let c = 2;
  let p = 1;
  let q = 1;
  let r = 0;
  let xNum = 0;
  let xDen = 1;
  let yNum = 0;
  let yDen = 1;

  while (true) {
    mode = Math.random() < 0.5 ? "xFromY" : "yFromX";
    a = randInt(-5, 5);
    if (a === 0) continue;
    b = randInt(-10, 10);
    c = randInt(2, 6); // celowo ulamkowe podstawianie
    p = randInt(-6, 6);
    q = randInt(-6, 6);
    if (p === 0 || q === 0) continue;
    fracLine = Math.random() < 0.5 ? 1 : 2;

    const tNum = randInt(-8, 8) || 1;
    const tDen = randInt(1, 4);

    if (mode === "xFromY") {
      // x = (a y + b)/c ; y = tNum/tDen
      yNum = tNum;
      yDen = tDen;
      xNum = a * yNum + b * yDen;
      xDen = c * yDen;
    } else {
      // y = (a x + b)/c ; x = tNum/tDen
      xNum = tNum;
      xDen = tDen;
      yNum = a * xNum + b * xDen;
      yDen = c * xDen;
    }

    [xNum, xDen] = simplifyFrac(xNum, xDen);
    [yNum, yDen] = simplifyFrac(yNum, yDen);

    // Po podstawieniu musi wyjsc rownanie z jedna niewiadoma (nie tozsamosc)
    if (mode === "xFromY" && p * a + q * c === 0) continue;
    if (mode === "yFromX" && p * c + q * a === 0) continue;

    const lx = xNum / xDen;
    const ly = yNum / yDen;
    r = p * lx + q * ly;
    if (!Number.isFinite(r)) continue;
    // chcemy calkowita prawa strone w drugim rownaniu
    if (Math.abs(r - Math.round(r)) > 1e-9) continue;
    r = Math.round(r);

    // ograniczenie "dzikosci"
    if (Math.abs(xNum) > 60 || Math.abs(yNum) > 60) continue;
    break;
  }

  let eqFrac = "";
  if (mode === "xFromY") {
    const ay = a === 1 ? "y" : a === -1 ? "-y" : `${a}y`;
    eqFrac = `x = \\dfrac{${ay} ${b >= 0 ? "+" : "-"} ${Math.abs(b)}}{${c}}`;
  } else {
    const ax = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
    eqFrac = `y = \\dfrac{${ax} ${b >= 0 ? "+" : "-"} ${Math.abs(b)}}{${c}}`;
  }
  const eqLin = buildLinearEquation(p, q, r);
  const systemLatex =
    fracLine === 1
      ? `\\begin{cases} ${eqFrac} \\\\ ${eqLin} \\end{cases}`
      : `\\begin{cases} ${eqLin} \\\\ ${eqFrac} \\end{cases}`;

  return {
    type: "LinearSystemSubstitutionFractionTask",
    data: {
      mode,
      a,
      b,
      c,
      p,
      q,
      r,
      xNum,
      xDen,
      yNum,
      yDen,
      fracLine,
      systemLatex,
      eqFrac,
      eqLin,
    },
  };
}

export function renderTask({ data }) {
  const prompt = data?.neutralPrompt
    ? "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144:"
    : "Rozwi\u0105\u017c uk\u0142ad r\u00f3wna\u0144 metod\u0105 podstawiania (z u\u0142amkiem):";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>{prompt}</p>
      <BlockMath math={data.systemLatex} />
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const xVal = userAnswer?.x || "";
  const yVal = userAnswer?.y || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"x ="} />
        <FractionInput
          value={xVal}
          onChange={(v) => onChange({ ...userAnswer, x: v })}
          disabled={disabled}
          label="x"
          placeholder="np. 3/2 lub 1.5"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y ="} />
        <FractionInput
          value={yVal}
          onChange={(v) => onChange({ ...userAnswer, y: v })}
          disabled={disabled}
          label="y"
          placeholder="np. -2/3 lub -0.67"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ux = parseVal(userAnswer?.x);
  const uy = parseVal(userAnswer?.y);
  if (!Number.isFinite(ux) || !Number.isFinite(uy)) return { correct: false };
  const ex = data.xNum / data.xDen;
  const ey = data.yNum / data.yDen;
  return { correct: Math.abs(ux - ex) < 0.01 && Math.abs(uy - ey) < 0.01 };
}

export function renderAnswer({ data }) {
  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      <div style={{ marginBottom: "0.12rem" }}>
        <InlineMath math={`x = ${fracToMixedLatex(data.xNum, data.xDen)}`} />
      </div>
      <div>
        <InlineMath math={`y = ${fracToMixedLatex(data.yNum, data.yDen)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const xRaw = userAnswer?.x?.raw || "";
  const yRaw = userAnswer?.y?.raw || "";
  if (!xRaw && !yRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div
      className="flex flex-col"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.25 }}
    >
      {xRaw ? (
        <div style={{ marginBottom: "0.12rem" }}>
          <InlineMath math={formatLatexPreview(xRaw, "x")} />
        </div>
      ) : (
        <i>(brak x)</i>
      )}
      {yRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(yRaw, "y")} />
        </div>
      ) : (
        <i>(brak y)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { mode, a, b, c, p, q, r, xNum, xDen, yNum, yDen, eqFrac, eqLin, fracLine } = data;
  const xLatex = fracToMixedLatex(xNum, xDen);
  const yLatex = fracToMixedLatex(yNum, yDen);

  const solvedVar = mode === "xFromY" ? "y" : "x";
  const solvedVal = mode === "xFromY" ? yLatex : xLatex;
  const solvedNum =
    mode === "xFromY" ? r * c - p * b : r * c - q * b;
  const solvedDen =
    mode === "xFromY" ? p * a + q * c : p * c + q * a;
  const solvedNumLatex = `${solvedNum}`;
  const solvedDenLatex = `${solvedDen}`;

  const substLine1 =
    mode === "xFromY"
      ? `${p}\\left(\\dfrac{${a === 1 ? "y" : a === -1 ? "-y" : `${a}y`} ${
          b >= 0 ? "+" : "-"
        } ${Math.abs(b)}}{${c}}\\right) ${q >= 0 ? "+" : "-"} ${absCoeffWithVar(
          q,
          "y"
        )} = ${r}`
      : `${absCoeffWithVar(p, "x")} ${q >= 0 ? "+" : "-"} ${Math.abs(q)}\\left(\\dfrac{${
          a === 1 ? "x" : a === -1 ? "-x" : `${a}x`
        } ${b >= 0 ? "+" : "-"} ${Math.abs(b)}}{${c}}\\right) = ${r}`;

  const substLine2 =
    mode === "xFromY"
      ? `${p}\\left(${a === 1 ? "y" : a === -1 ? "-y" : `${a}y`} ${
          b >= 0 ? "+" : "-"
        } ${Math.abs(b)}\\right) ${q >= 0 ? "+" : "-"} ${absCoeffWithVar(
          q * c,
          "y"
        )} = ${r * c}`
      : `${absCoeffWithVar(p * c, "x")} ${q >= 0 ? "+" : "-"} ${Math.abs(q)}\\left(${
          a === 1 ? "x" : a === -1 ? "-x" : `${a}x`
        } ${b >= 0 ? "+" : "-"} ${Math.abs(b)}\\right) = ${r * c}`;

  const substLine3 =
    mode === "xFromY"
      ? `${absCoeffWithVar(p * a, "y")} ${p * b >= 0 ? "+" : "-"} ${Math.abs(p * b)} ${
          q * c >= 0 ? "+" : "-"
        } ${absCoeffWithVar(q * c, "y")} = ${r * c}`
      : `${absCoeffWithVar(p * c, "x")} ${q * a >= 0 ? "+" : "-"} ${absCoeffWithVar(
          q * a,
          "x"
        )} ${
          q * b >= 0 ? "+" : "-"
        } ${Math.abs(q * b)} = ${r * c}`;

  const substLine4 = `${solvedDenLatex}${solvedVar} = ${solvedNumLatex}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Przepisujemy układ i podstawiamy wyrażenie z równania z ułamkiem do drugiego:
      </p>
      <BlockMath
        math={String.raw`\begin{cases}
${fracLine === 1 ? eqFrac : eqLin}\\
${fracLine === 1 ? eqLin : eqFrac}
\end{cases}`}
      />
      <BlockMath math={substLine1} />

      <p>
        <strong>Krok 2:</strong> Rozwiązujemy otrzymane równanie i wyznaczamy{" "}
        <InlineMath math={solvedVar} /> (mnożymy obie strony przez{" "}
        <InlineMath math={`${c}`} />, aby usunąć mianownik):
      </p>
      <BlockMath math={substLine2} />
      <BlockMath math={substLine3} />
      <BlockMath math={substLine4} />
      <BlockMath math={`${solvedVar} = ${solvedVal}`} />

      <p>
        <strong>Krok 3:</strong> Podstawiamy do pierwszego równania i liczymy drugą niewiadomą:
      </p>
      <BlockMath math={`x = ${xLatex},\\quad y = ${yLatex}`} />

      <p>
        <strong>Odpowiedź:</strong>{" "}
        <InlineMath math={`\\left(x,y\\right)=\\left(${xLatex}, ${yLatex}\\right)`} />
      </p>
    </div>
  );
}



